import { useEffect } from 'react';

interface SEOOptions {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  articleData?: {
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
    section?: string;
    tags?: string[];
  };
  structuredData?: any;
  noindex?: boolean;
  nofollow?: boolean;
}

export const useSEO = (options: SEOOptions) => {
  useEffect(() => {
    const {
      title = "Smartborn - Leading IT Services & Software Development Company",
      description = "Transform your business with Smartborn's innovative IT services, offshore development, and staff augmentation. Expert solutions across 25+ domains.",
      keywords = "IT services, software development, offshore development, staff augmentation, SAP, Salesforce, Workday, enterprise solutions",
      canonical,
      ogImage = "/lovable-uploads/191658a8-ce44-4edc-a729-a8d8b92a2d8b.png",
      ogType = "website",
      articleData,
      structuredData,
      noindex = false,
      nofollow = false
    } = options;

    // Update document title
    document.title = title;

    // Helper function to update or create meta tag
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    // Robots meta tag
    const robotsContent = [];
    if (noindex) robotsContent.push('noindex');
    else robotsContent.push('index');
    if (nofollow) robotsContent.push('nofollow');
    else robotsContent.push('follow');
    
    updateMetaTag('robots', robotsContent.join(', '));

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', window.location.href, true);
    updateMetaTag('og:image', ogImage.startsWith('http') ? ogImage : `${window.location.origin}${ogImage}`, true);
    updateMetaTag('og:site_name', 'Smartborn', true);

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', ogImage.startsWith('http') ? ogImage : `${window.location.origin}${ogImage}`, true);

    // Article-specific meta tags
    if (articleData && ogType === 'article') {
      if (articleData.author) updateMetaTag('article:author', articleData.author, true);
      if (articleData.publishedTime) updateMetaTag('article:published_time', articleData.publishedTime, true);
      if (articleData.modifiedTime) updateMetaTag('article:modified_time', articleData.modifiedTime, true);
      if (articleData.section) updateMetaTag('article:section', articleData.section, true);
      if (articleData.tags) {
        articleData.tags.forEach(tag => {
          const meta = document.createElement('meta');
          meta.setAttribute('property', 'article:tag');
          meta.setAttribute('content', tag);
          document.head.appendChild(meta);
        });
      }
    }

    // Canonical URL
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical.startsWith('http') ? canonical : `${window.location.origin}${canonical}`);
    }

    // Structured data
    if (structuredData) {
      const existingScript = document.querySelector('script[type="application/ld+json"][data-seo-hook]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-hook', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Add hreflang for international SEO (if applicable)
    const addHreflang = (lang: string, url: string) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', url);
      document.head.appendChild(link);
    };

    // Example hreflang tags (customize based on your international presence)
    // addHreflang('en', window.location.href);
    // addHreflang('es', window.location.href.replace('/en/', '/es/'));

    return () => {
      // Cleanup function to remove article tags when component unmounts
      if (articleData && ogType === 'article' && articleData.tags) {
        const articleTagMetas = document.querySelectorAll('meta[property="article:tag"]');
        articleTagMetas.forEach(meta => meta.remove());
      }
    };
  }, [options]);
};

// Hook for tracking Core Web Vitals
export const useCoreWebVitals = () => {
  useEffect(() => {
    if (typeof window !== 'undefined' && 'web-vitals' in window) {
      // This would require installing web-vitals package
      // import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';
      
      const reportWebVital = (metric: any) => {
        // Send to analytics
        if (window.gtag) {
          window.gtag('event', metric.name, {
            event_category: 'Web Vitals',
            event_label: metric.id,
            value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
            non_interaction: true,
          });
        }
      };

      // Uncomment when web-vitals is installed
      // getCLS(reportWebVital);
      // getFID(reportWebVital);
      // getFCP(reportWebVital);
      // getLCP(reportWebVital);
      // getTTFB(reportWebVital);
    }
  }, []);
};

// Hook for managing breadcrumbs
export const useBreadcrumbs = (breadcrumbs: Array<{ name: string; url: string }>) => {
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": `${window.location.origin}${item.url}`
      }))
    };

    const existingScript = document.querySelector('script[type="application/ld+json"][data-breadcrumb]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-breadcrumb', 'true');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"][data-breadcrumb]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [breadcrumbs]);
};